import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, functions, httpsCallable } from '../config/firebase';
import { signOut, onAuthStateChanged, User } from 'firebase/auth';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthRequiredLink from '../components/AuthRequiredLink';
import { usePageInit } from '../hooks/usePageInit';
import './AccountDeletionPage.css';

export default function AccountDeletionPage() {
  usePageInit();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const requestAccountDeletion = async () => {
    if (!user) {
      setError('ログインが必要です');
      return;
    }

    try {
      setDeletingAccount(true);
      setError(null);
      const requestDeletion = httpsCallable(functions, 'requestAccountDeletionV2');
      await requestDeletion({});
      
      // 成功後、即座にログアウト
      try {
        await signOut(auth);
        setSuccess(true);
        // 3秒後にホームにリダイレクト
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } catch (signOutError) {
        console.error('Sign out error:', signOutError);
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } catch (e: any) {
      console.error('requestAccountDeletion failed:', e);
      const errorMessage = e?.message || 'アカウント削除の申請に失敗しました。時間をおいて再試行してください。';
      setError(errorMessage);
    } finally {
      setDeletingAccount(false);
    }
  };

  const confirmAccountDeletion = () => {
    const msg =
      'アカウント削除を申請します。\n\n' +
      '・申請後は即時に利用停止（ログイン不可）になります\n' +
      '・取消/復旧はできません\n' +
      '・チャット本文は匿名化して残ります\n' +
      '・画像/ファイル等の添付は60日間保持されますが閲覧できず、60日後に削除されます\n' +
      '・領収/明細などの取引記録は法令・正当目的のため保持されます（教室の屋号は表示されます）\n\n' +
      '本当に申請しますか？';
    
    if (window.confirm(msg)) {
      requestAccountDeletion();
    }
  };

  if (loading) {
    return (
      <div className="m-application theme--light transition-page" id="app">
        <div className="loading"></div>
        <div className="m-content joker joker-var" id="main-wrap">
          <div className="main-wrap">
            <Header />
            <div className="policy-page">
              <div className="page-wrap">
                <div className="container inner-wrap max-md">
                  <div className="card policy-box fragment-fadeUp">
                    <div className="policy-content">
                      <div className="loading-container">
                        <p>読み込み中...</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footer withDeco={true} />
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="m-application theme--light transition-page" id="app">
      <div className="loading"></div>
      <div className="m-content joker joker-var" id="main-wrap">
        <div className="main-wrap">
          <Header />
          <div className="policy-page">
            <div className="page-wrap">
              <div className="hidden-md-up">
                <div className="logo logo-header">
                  <AuthRequiredLink to="/">
                    <img src="/assets/images/mobile-logo.png" alt="logo" />
                  </AuthRequiredLink>
                </div>
              </div>
              <div className="container inner-wrap max-md">
                <AuthRequiredLink className="waves-effect btn-icon backtohome fragment-fadeUp" to="/">
                  <span>
                    <i className="ion-ios-home-outline"></i>
                    <i className="ion-ios-arrow-thin-left"></i>
                  </span>
                </AuthRequiredLink>
                <div className="card policy-box fragment-fadeUp">
                  <div className="policy-content">
                    <h1 className="policy-title">アカウント削除</h1>
                    {success ? (
                      <div className="policy-body">
                        <div className="account-deletion-success">
                          <p>アカウント削除の申請が完了しました。</p>
                          <p>ご利用ありがとうございました。</p>
                          <p className="redirect-message">ホームページにリダイレクトします...</p>
                        </div>
                      </div>
                    ) : !user ? (
                      <div className="policy-body">
                        <div className="account-deletion-content">
                          <p className="account-deletion-description">
                            アカウントを削除するには、ログインが必要です。
                          </p>
                          
                          <div className="account-deletion-login-prompt">
                            <p>
                              アカウント削除を行うには、まずログインしてください。
                            </p>
                            <div className="account-deletion-actions">
                              <Link
                                to="/login"
                                state={{ from: '/account-deletion' }}
                                className="btn btn-primary waves-effect"
                              >
                                ログインページへ
                              </Link>
                            </div>
                          </div>

                          <div className="account-deletion-warning">
                            <h3>削除に関する注意事項</h3>
                            <ul>
                              <li>申請後は即時に利用停止（ログイン不可）になります</li>
                              <li>取消/復旧はできません</li>
                              <li>チャット本文は匿名化して残ります</li>
                              <li>画像/ファイル等の添付は60日間保持されますが閲覧できず、60日後に削除されます</li>
                              <li>領収/明細などの取引記録は法令・正当目的のため保持されます（教室の屋号は表示されます）</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="policy-body">
                        <div className="account-deletion-content">
                          <p className="account-deletion-description">
                            アカウントを削除すると、即時に利用停止となり、60日後に個人情報の削除/匿名化と添付の削除が行われます（取消不可）。
                          </p>
                          
                          {error && (
                            <div className="account-deletion-error">
                              <p>{error}</p>
                            </div>
                          )}

                          <div className="account-deletion-actions">
                            <button
                              className="btn btn-danger waves-effect"
                              onClick={confirmAccountDeletion}
                              disabled={deletingAccount}
                            >
                              {deletingAccount ? '処理中...' : 'アカウントを削除する'}
                            </button>
                          </div>

                          <div className="account-deletion-warning">
                            <h3>削除に関する注意事項</h3>
                            <ul>
                              <li>申請後は即時に利用停止（ログイン不可）になります</li>
                              <li>取消/復旧はできません</li>
                              <li>チャット本文は匿名化して残ります</li>
                              <li>画像/ファイル等の添付は60日間保持されますが閲覧できず、60日後に削除されます</li>
                              <li>領収/明細などの取引記録は法令・正当目的のため保持されます（教室の屋号は表示されます）</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer withDeco={true} />
        </div>
      </div>
    </div>
  );
}

